import type { ReactNode } from 'react'
import type { CiScan, ScanIdentity, ScanPort } from '../api/cmdb'

/**
 * Ce a gasit scanarea despre un aparat FARA AGENT - fisa unui CI nascut din
 * ADM > Pending > Add to CMDB (`refType` 2).
 *
 * De ce exista (devora, /app/cmdb/view/35, 14 octombrie 2026): fisa citea
 * numai inventarul (cardul Asset) si ce raporteaza agentul de pe masina
 * (Observed by the relay). Un aparat descoperit nu le are pe niciuna, deci
 * fisa iesea goala, desi scanarea trimisese nume, adresa, MAC, TTL, sistemul
 * ghicit, porturile si amprentele serviciilor.
 *
 * Toate cuvintele si toate valorile vin gata puse de server
 * (`DiscoveredCiCard`); aici doar se deseneaza.
 */

const DASH = '—'

/** Randurile identitatii, in ordinea in care le cauta omul. */
function identityRows(id: ScanIdentity): [string, ReactNode][] {
  return [
    ['Host name', id.hostname],
    ['IP address', id.ip],
    ['MAC address', id.mac],
    ['Manufacturer', id.manufacturer !== null ? <>{id.manufacturer} <span className="adm-muted">(from the MAC)</span></> : null],
    [
      'Operating system',
      id.os !== null ? (
        <>
          {id.os}
          {id.osGuessed && (
            <span className="adm-pill" style={{ marginLeft: 6 }} title="Guessed by the scanner from the TTL or the open ports, not read from the device">
              guessed
            </span>
          )}
        </>
      ) : null,
    ],
    ['Looks like', id.kind],
    ['TTL', id.ttl],
    ['Ping', id.pingMs !== null ? `${id.pingMs} ms` : null],
    ['Found by relay', id.relayName ?? (id.relayId !== null ? `#${id.relayId}` : null)],
    ['First seen', id.firstSeen],
    ['Last seen', id.lastSeen],
    ['Runs on host', id.virtualHost],
  ]
}

/** Cardul de identitate - in locul cardului Asset, pe care un aparat descoperit nu-l are. */
export function ScanIdentityCard({ scan }: { scan: CiScan }) {
  return (
    <div className="adm-card" id="cmdbScanIdentity">
      <div className="adm-card-head">
        <h5>
          <i className="fas fa-satellite-dish"></i> Found by the network scan
        </h5>
        <span className="adm-card-note">no agent</span>
      </div>
      <table className="table adm-list">
        <tbody>
          {identityRows(scan.identity).map(([label, value]) => (
            <tr key={label}>
              <td style={{ width: '40%' }}>{label}</td>
              <td>{value === null || value === '' ? <span className="adm-muted">{DASH}</span> : value}</td>
            </tr>
          ))}
          {scan.hints.length > 0 && (
            <tr>
              <td>Scanner hints</td>
              <td>
                {scan.hints.map((hint) => (
                  <span key={hint} className="adm-pill" style={{ marginRight: 4 }}>
                    {hint}
                  </span>
                ))}
              </td>
            </tr>
          )}
          {scan.services !== '' && (
            <tr>
              <td>Answered</td>
              <td className="adm-muted" style={{ fontSize: 12, wordBreak: 'break-word' }}>
                {scan.services}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="adm-muted small" style={{ padding: '0 12px 10px' }}>
        {scan.found ? scan.note : 'The discovered device is no longer in Discovered; this is what was known when it was last scanned.'}
      </p>
    </div>
  )
}

/** Porturile deschise vazute de scanare, cu amprenta fiecarui serviciu. */
export function ScanPortsTable({ ports }: { ports: ScanPort[] }) {
  if (ports.length === 0) {
    return (
      <div className="adm-empty-inline">
        <i className="fas fa-ethernet"></i> The scan found no open port on this device.
      </div>
    )
  }
  return (
    <table className="table adm-list">
      <thead>
        <tr>
          <th style={{ width: 82 }} className="adm-numeric">
            Port
          </th>
          <th>Service</th>
          <th>What it answered</th>
        </tr>
      </thead>
      <tbody>
        {ports.map((row) => (
          <tr key={`${row.proto}${row.port}`}>
            <td className="adm-numeric">
              <strong>{row.port}</strong>{' '}
              <span className="adm-muted" style={{ fontSize: 11 }}>
                {row.proto.toUpperCase()}
              </span>
            </td>
            <td>
              {row.service !== '' ? row.service : DASH}{' '}
              {row.watch !== null && (
                <span className="adm-pill adm-pill-warn" title="Administration or sharing port - worth a second look">
                  {row.watch}
                </span>
              )}
            </td>
            <td className="adm-muted" style={{ fontSize: 12, wordBreak: 'break-word' }}>
              {row.banner !== '' ? row.banner : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * In locul lui „Observed by the relay": pe un aparat fara agent nu raporteaza
 * nimeni din interior. Ce se stie e ce a gasit scanarea deschis, cine il
 * foloseste dupa telemetria ALTOR masini, si cand a fost vazut.
 */
export function ScanSection({ scan }: { scan: CiScan }) {
  return (
    <>
      <div className="adm-section">
        <h4>Seen by the network scan</h4>
        <span className="adm-section-note" id="cmdbScanNote">
          no agent - data only from the scan
          {scan.identity.lastSeen !== null && <>, last seen {scan.identity.lastSeen}</>}
        </span>
      </div>
      <div className="row">
        <div className="col-md-5">
          <div className="adm-card adm-tone-blue-hoki" id="cmdbScanPorts">
            <div className="adm-card-head">
              <h5>
                <i className="fas fa-ethernet"></i> Open to the network
              </h5>
              <span className="adm-card-note">
                {scan.ports.length} port{scan.ports.length === 1 ? '' : 's'}
              </span>
            </div>
            <ScanPortsTable ports={scan.ports} />
          </div>
        </div>
        <div className="col-md-7">
          <div className="adm-card adm-tone-blue-mauve" id="cmdbScanPeers">
            <div className="adm-card-head">
              <h5>
                <i className="fas fa-exchange-alt"></i> Talks to machines with an agent
              </h5>
              <span className="adm-card-note">from their telemetry</span>
            </div>
            {scan.peers.length === 0 ? (
              <div className="adm-empty-inline">
                <i className="fas fa-exchange-alt"></i> No machine with an agent has reported a connection to this device.
              </div>
            ) : (
              <table className="table adm-list">
                <thead>
                  <tr>
                    <th>Machine</th>
                    <th style={{ width: 110 }}></th>
                    <th style={{ width: 96 }} className="adm-numeric">
                      Port
                    </th>
                    <th>Application</th>
                    <th style={{ width: 78 }} className="adm-numeric">
                      Seen
                    </th>
                    <th style={{ width: 118 }}>Last</th>
                  </tr>
                </thead>
                <tbody>
                  {scan.peers.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <a href={`/app/cmdb/view/${row.ciId}`} target="_blank" rel="noopener">
                          {row.ciName}
                        </a>
                      </td>
                      <td className="adm-muted">{row.direction}</td>
                      <td className="adm-numeric">
                        {row.port}{' '}
                        <span className="adm-muted" style={{ fontSize: 11 }}>
                          {row.proto}
                        </span>
                      </td>
                      <td className="adm-muted" style={{ fontSize: 12 }}>
                        {row.application ?? DASH}
                      </td>
                      <td className="adm-numeric adm-muted">{row.seen.toLocaleString('en-US')}</td>
                      <td className="adm-muted" style={{ fontSize: 11.5 }}>
                        {row.lastSeen ?? ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <div className="adm-card" id="cmdbScanHistory">
        <div className="adm-card-head">
          <h5>
            <i className="fas fa-history"></i> Scan history
          </h5>
          <span className="adm-card-note">the newest {scan.history.length} runs that saw it</span>
        </div>
        {scan.history.length === 0 ? (
          <div className="adm-empty-inline">
            <i className="fas fa-history"></i> No scan run is linked to this device yet.
          </div>
        ) : (
          <table className="table adm-list">
            <tbody>
              {scan.history.map((run) => (
                <tr key={run.runId}>
                  <td className="adm-nowrap adm-muted" style={{ width: 140 }}>
                    {run.at ?? DASH}
                  </td>
                  <td>
                    <a href={`/app/adm/run/${run.runId}`} target="_blank" rel="noopener">
                      {run.type !== '' ? run.type : 'Run'} #{run.runId}
                    </a>
                  </td>
                  <td className="adm-muted">{run.outcome ?? DASH}</td>
                  <td className="adm-muted">{[run.hostname, run.ip].filter(Boolean).join(' · ')}</td>
                  <td className="adm-muted adm-numeric">{run.ttl !== null ? `TTL ${run.ttl}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}
